import { Suspense, useRef, useState, useMemo, useEffect } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls, Html, Stars } from "@react-three/drei";
import { XR, createXRStore } from "@react-three/xr";
import { TextureLoader, BackSide } from "three";
import { motion } from "framer-motion";
import { imgUrl } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Info, ChevronLeft, ChevronRight, Volume2, VolumeX, Play, Pause, Glasses,
  ClipboardList, CheckCircle2, XCircle, Award, X, User, MessageCircle,
  RotateCcw, Film, Footprints, Route, LogOut, ThumbsUp, ThumbsDown, HelpCircle,
} from "lucide-react";

// Build an ordered "beat" list for a scene. Prefers an AI-sequenced beats[]
// (added on regeneration); otherwise falls back to a cheap heuristic:
// scene intro narration, then reveal each hotspot one at a time.
function buildBeats(sc) {
  if (Array.isArray(sc?.beats) && sc.beats.length) {
    return sc.beats.map((b) => {
      if (b.type === "hotspot")
        return { type: "hotspot", hotspot_index: b.hotspot_index ?? b.index ?? 0, text: b.text || "" };
      if (b.type === "checkin")
        return {
          type: "checkin",
          kind: b.kind === "mcq" ? "mcq" : "pulse",
          prompt: b.prompt || "",
          question: b.question || "",
          options: b.options || [],
          answer: b.answer || "",
        };
      return { type: "narration", text: b.text || "" };
    });
  }
  const beats = [];
  if (sc?.narration) beats.push({ type: "narration", text: sc.narration });
  (sc?.hotspots || []).forEach((h, i) =>
    beats.push({ type: "hotspot", hotspot_index: i, text: `${h.title || ""}. ${h.info || ""}`.trim() })
  );
  if (!beats.length) beats.push({ type: "narration", text: sc?.title || "" });
  return beats;
}

function SkyboxTexture({ url }) {
  const texture = useLoader(TextureLoader, imgUrl(url));
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[60, 64, 44]} />
      <meshBasicMaterial map={texture} side={BackSide} />
    </mesh>
  );
}

function Skybox({ url, color }) {
  if (url)
    return (
      <Suspense
        fallback={
          <mesh scale={[-1, 1, 1]}>
            <sphereGeometry args={[60, 40, 40]} />
            <meshBasicMaterial color={color || "#0B0F19"} side={BackSide} />
          </mesh>
        }
      >
        <SkyboxTexture url={url} />
      </Suspense>
    );
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[60, 40, 40]} />
      <meshBasicMaterial color={color || "#0B0F19"} side={BackSide} />
    </mesh>
  );
}

function CharacterMarker({ ch }) {
  const [open, setOpen] = useState(false);
  const pos = ch.position || [3, 1, -4];
  return (
    <Html position={pos} center distanceFactor={14} occlude={false}>
      <div className="flex flex-col items-center">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold shadow-xl whitespace-nowrap hover:bg-emerald-600 transition-colors"
        >
          <User className="w-3.5 h-3.5" /> {ch.name || "Guide"}
          <MessageCircle className="w-3.5 h-3.5 opacity-80" />
        </button>
        {open && ch.dialogue && (
          <div className="mt-1.5 max-w-[200px] px-3 py-2 rounded-xl bg-white text-slate-800 text-xs font-medium shadow-2xl">
            “{ch.dialogue}”
          </div>
        )}
      </div>
    </Html>
  );
}

function GuideAvatar({ guide, speaking, label }) {
  if (!guide?.image_url) return null;
  return (
    <Html position={[3.4, -1.4, -3]} center distanceFactor={11} occlude={false}>
      <div className="flex flex-col items-center pointer-events-none select-none" data-testid="vr-guide-avatar" style={{ pointerEvents: "none" }}>
        {label && (
          <div className="mb-2 w-[220px] px-3 py-2 rounded-2xl bg-white text-slate-800 text-xs font-medium shadow-2xl leading-snug break-words text-center">
            {label}
          </div>
        )}
        <motion.div
          animate={speaking ? { y: [0, -7, 0], scale: [1, 1.03, 1] } : { y: [0, -3, 0] }}
          transition={{ duration: speaking ? 0.55 : 2.6, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          {speaking && <span className="absolute -inset-4 rounded-full bg-primary/40 blur-2xl animate-pulse" />}
          <img src={imgUrl(guide.image_url)} alt={guide.name} className="relative h-56 object-contain drop-shadow-2xl" />
        </motion.div>
        <div className="mt-1 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold shadow whitespace-nowrap">
          {guide.name || "Guide"}
        </div>
      </div>
    </Html>
  );
}

function LayerTexture({ layer }) {
  const texture = useLoader(TextureLoader, imgUrl(layer.image_url));
  const scale = layer.scale || 4;
  const aspect = (texture.image && texture.image.width && texture.image.height)
    ? texture.image.width / texture.image.height : 1;
  return (
    <mesh position={layer.position || [0, 0.4, -8]}>
      <planeGeometry args={[scale * aspect, scale]} />
      <meshBasicMaterial map={texture} transparent alphaTest={0.05} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

function LayerPlane({ layer }) {
  if (!layer?.image_url) return null;
  return (
    <Suspense fallback={null}>
      <LayerTexture layer={layer} />
    </Suspense>
  );
}

function HotspotMarker({ hs, forceOpen, onOpen }) {
  const [open, setOpen] = useState(false);
  const pos = hs.position || [-3, 2, -4];
  const show = open || forceOpen;
  const toggle = () => {
    setOpen((o) => {
      if (!o && onOpen) onOpen(hs);
      return !o;
    });
  };
  return (
    <Html position={pos} center distanceFactor={14} occlude={false}>
      <div className="flex flex-col items-center">
        <button
          data-testid="vr-hotspot-btn"
          onClick={toggle}
          className={`w-8 h-8 rounded-full ${forceOpen ? "bg-amber-400 ring-4 ring-amber-300/60" : "bg-cyan-400/95"} border-2 border-white flex items-center justify-center ${show ? "" : "animate-pulse"} shadow-xl`}
        >
          <Info className="w-4 h-4 text-white" />
        </button>
        {show && (
          <div className="mt-1.5 w-[240px] rounded-xl bg-cyan-950 text-cyan-50 shadow-2xl border border-cyan-500/40 overflow-hidden" data-testid="vr-hotspot-card">
            {hs.reveal_image_url && (
              <img src={imgUrl(hs.reveal_image_url)} alt={hs.title} className="w-full h-32 object-cover" />
            )}
            <div className="px-3 py-2">
              <div className="font-bold mb-0.5">{hs.title}</div>
              <div className="opacity-90 leading-snug text-xs">{hs.info}</div>
            </div>
          </div>
        )}
      </div>
    </Html>
  );
}

function CheckinBeat({ beat, onDone }) {
  const [picked, setPicked] = useState(null);
  const answerLetter = String(beat.answer || "").trim().toUpperCase()[0];
  const optLetter = (o) => String(o || "").trim().toUpperCase()[0];
  if (beat.kind === "mcq") {
    return (
      <div className="mt-3 rounded-xl bg-secondary/60 border border-border p-3" data-testid="vr-checkin-mcq">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-600 mb-1">
          <HelpCircle className="w-3.5 h-3.5" /> Quick check
        </div>
        <p className="text-sm font-semibold mb-2">{beat.question || beat.prompt}</p>
        <div className="space-y-1.5">
          {(beat.options || []).map((opt, i) => {
            const isCorrect = optLetter(opt) === answerLetter;
            const isSel = picked === opt;
            let cls = "border-border hover:border-primary";
            if (picked && isCorrect) cls = "border-emerald-500 bg-emerald-50 text-emerald-800";
            else if (picked && isSel && !isCorrect) cls = "border-red-500 bg-red-50 text-red-800";
            return (
              <button
                key={i}
                data-testid={`vr-checkin-option-${i}`}
                disabled={!!picked}
                onClick={() => setPicked(opt)}
                className={`w-full text-left text-xs rounded-lg border px-3 py-2 transition-colors ${cls}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
        {picked && (
          <button
            data-testid="vr-checkin-continue-btn"
            onClick={() => onDone(picked, optLetter(picked) === answerLetter)}
            className="mt-2 w-full h-9 rounded-full bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600"
          >
            Continue
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl bg-secondary/60 border border-border p-3 text-center" data-testid="vr-checkin-pulse">
      <p className="text-sm font-semibold mb-2">{beat.prompt || "Did that make sense?"}</p>
      <div className="flex items-center justify-center gap-2">
        <button
          data-testid="vr-checkin-gotit-btn"
          onClick={() => onDone("got_it", null)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600"
        >
          <ThumbsUp className="w-4 h-4" /> Got it
        </button>
        <button
          data-testid="vr-checkin-confused-btn"
          onClick={() => onDone("confused", null)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-500 text-white text-xs font-bold hover:bg-amber-600"
        >
          <ThumbsDown className="w-4 h-4" /> Confused
        </button>
      </div>
    </div>
  );
}

function SceneContent({ sc, sceneKey, guided, visibleHotspots, currentHotspot, onHotspotOpen }) {
  return (
    <>
      <Skybox key={sceneKey} url={sc.backdrop_clean_url || sc.backdrop_url} color={sc.ambient_color} />
      {!sc.backdrop_url && <Stars radius={50} depth={30} count={2500} factor={4} fade speed={1} />}
      {(sc.layers || []).map((l, i) => (
        <LayerPlane key={"l" + i} layer={l} />
      ))}
      {(sc.characters || []).map((c, i) => (
        <CharacterMarker key={"c" + i} ch={c} />
      ))}
      {(sc.hotspots || []).map((h, i) => {
        if (guided && !visibleHotspots.includes(i)) return null;
        return (
          <HotspotMarker
            key={"h" + i}
            hs={h}
            forceOpen={guided ? currentHotspot === i : false}
            onOpen={onHotspotOpen}
          />
        );
      })}
    </>
  );
}

export default function VRViewer({ scene, autoRotate = true, quiz = [], lessonId = null, students = [], onQuizSubmitted, defaultStudentId = null }) {
  const store = useMemo(() => createXRStore(), []);
  const scenes = useMemo(() => {
    if (!scene) return [];
    if (Array.isArray(scene.scenes) && scene.scenes.length) return scene.scenes;
    const env = scene.environment || {};
    return [
      {
        title: env.name || "VR Scene",
        backdrop_url: env.skybox_url,
        ambient_color: env.skybox_color,
        characters: scene.characters || [],
        hotspots: scene.hotspots || [],
      },
    ];
  }, [scene]);

  const guideData = scene?.guide;

  const [idx, setIdx] = useState(0);
  const [narrate, setNarrate] = useState(false);
  const [tour, setTour] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [xrSupported, setXrSupported] = useState(false);

  // ---- Scene timeline (beat-by-beat guided player) ----
  const [guided, setGuided] = useState(false);
  const [cinematic, setCinematic] = useState(false);
  const [beatIdx, setBeatIdx] = useState(0);

  const audioRef = useRef(null);
  const ttsCache = useRef({});
  const tourRef = useRef(tour);
  const fallbackTimer = useRef(null);
  const advanceBeatRef = useRef(() => {});
  tourRef.current = tour;

  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  useEffect(() => {
    let mounted = true;
    if (typeof navigator !== "undefined" && navigator.xr?.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then((ok) => mounted && setXrSupported(!!ok)).catch(() => {});
    }
    return () => {
      mounted = false;
    };
  }, []);

  const enterVR = async () => {
    try {
      await store.enterVR();
    } catch (e) {
      toast.error("No VR headset detected on this device");
    }
  };

  const len = scenes.length;
  const goNext = () =>
    setIdx((i) => {
      if (i >= len - 1) {
        setTour(false);
        return i;
      }
      return i + 1;
    });

  const stopAudio = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.onended = null;
    }
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    setSpeaking(false);
  };

  // Generic TTS playback. onEnd (when provided) fires when narration finishes
  // (or after a fallback timer when there is no audio) — used for auto-advance.
  const speak = async (text, cacheKey, onEnd) => {
    stopAudio();
    if (!text) {
      if (onEnd) fallbackTimer.current = setTimeout(onEnd, 3200);
      return;
    }
    try {
      let url = ttsCache.current[cacheKey];
      if (!url) {
        const { data } = await api.post("/tts", { text });
        url = data.url;
        ttsCache.current[cacheKey] = url;
      }
      const a = audioRef.current;
      a.src = imgUrl(url);
      a.onended = () => {
        setSpeaking(false);
        if (onEnd) onEnd();
      };
      await a.play();
      setSpeaking(true);
    } catch (e) {
      setSpeaking(false);
      if (onEnd) fallbackTimer.current = setTimeout(onEnd, 4000);
    }
  };

  const playNarration = (index) => {
    const sc = scenes[index];
    speak(sc?.narration, `scene:${index}`, tourRef.current ? goNext : null);
  };

  // Narrate/auto-tour audio (only when NOT in guided mode — guided owns audio)
  useEffect(() => {
    if (guided) return;
    if (narrate) playNarration(idx);
    else stopAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, narrate, guided]);

  useEffect(() => {
    if (guided || !tour || narrate) return;
    const t = setTimeout(goNext, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, narrate, idx, guided]);

  useEffect(() => () => stopAudio(), []);

  // ---- beat computation ----
  const beats = useMemo(
    () => buildBeats(scenes[Math.min(idx, Math.max(0, len - 1))] || {}),
    [scenes, idx, len]
  );
  const clampedBeat = Math.min(beatIdx, Math.max(0, beats.length - 1));
  const curBeat = beats[clampedBeat];
  const visibleHotspots = useMemo(() => {
    const arr = [];
    for (let i = 0; i <= clampedBeat && i < beats.length; i++) {
      if (beats[i]?.type === "hotspot") arr.push(beats[i].hotspot_index);
    }
    return arr;
  }, [beats, clampedBeat]);
  const currentHotspot = curBeat?.type === "hotspot" ? curBeat.hotspot_index : -1;

  const beatSpokenText = (b) => (b?.type === "checkin" ? b.question || b.prompt || "" : b?.text || "");
  const beatKey = (b, i) =>
    b?.type === "hotspot" ? `hs:${idx}:${b.hotspot_index}` : b?.type === "checkin" ? `chk:${idx}:${i}` : `narr:${idx}:${i}`;

  const advanceBeat = () => {
    if (clampedBeat < beats.length - 1) setBeatIdx(clampedBeat + 1);
    else if (idx < len - 1) setIdx(idx + 1); // beatIdx resets via idx effect below
  };
  advanceBeatRef.current = advanceBeat;

  // reset to first beat whenever the scene changes
  useEffect(() => {
    setBeatIdx(0);
  }, [idx]);

  // guided beat player: narrate the current beat, auto-advance in cinematic mode
  // (check-in beats never auto-advance — they wait for the student's response)
  useEffect(() => {
    if (!guided) return;
    const b = beats[clampedBeat];
    if (!b) return;
    const autoAdvance = cinematic && b.type !== "checkin";
    speak(beatSpokenText(b), beatKey(b, clampedBeat), autoAdvance ? () => advanceBeatRef.current() : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guided, clampedBeat, idx, cinematic]);

  const startGuided = () => {
    setNarrate(false);
    setTour(false);
    setBeatIdx(0);
    setGuided(true);
  };
  const exitGuided = () => {
    setGuided(false);
    stopAudio();
  };
  const replayBeat = () => {
    const b = beats[clampedBeat];
    const autoAdvance = cinematic && b?.type !== "checkin";
    speak(beatSpokenText(b), beatKey(b, clampedBeat), autoAdvance ? () => advanceBeatRef.current() : null);
  };
  const narrateHotspot = (hs) => {
    if (guided) return; // guided mode already narrates on reveal
    speak(`${hs.title || ""}. ${hs.info || ""}`.trim(), `hsopen:${hs.title || ""}`);
  };
  const respondCheckin = async (response, correct) => {
    const b = beats[clampedBeat];
    if (lessonId) {
      try {
        await api.post(`/lessons/${lessonId}/checkin-response`, {
          scene_index: idx,
          beat_index: clampedBeat,
          kind: b?.kind || "pulse",
          prompt: b?.question || b?.prompt || "",
          response,
          correct,
          student_id: defaultStudentId || null,
        });
      } catch (e) {
        /* recording best-effort */
      }
    }
    advanceBeat();
  };

  // ---- In-VR quiz ----
  const questions = Array.isArray(quiz) ? quiz : [];
  const [quizOpen, setQuizOpen] = useState(false);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [chosenStudent, setChosenStudent] = useState(defaultStudentId || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const answerLetter = (q) => String(q?.answer || "").trim().toUpperCase()[0];
  const optLetter = (opt) => String(opt || "").trim().toUpperCase()[0];

  const startQuiz = () => {
    stopAudio();
    setNarrate(false);
    setTour(false);
    setQuizOpen(true);
    setQIdx(0);
    setSelected(null);
    setRevealed(false);
    setScore(0);
    setFinished(false);
    setSubmitted(false);
    setChosenStudent(defaultStudentId || "");
  };

  const pickOption = (opt) => {
    if (revealed) return;
    setSelected(opt);
    setRevealed(true);
    if (optLetter(opt) === answerLetter(questions[qIdx])) setScore((s) => s + 1);
  };

  const nextQuestion = () => {
    if (qIdx < questions.length - 1) {
      setQIdx((i) => i + 1);
      setSelected(null);
      setRevealed(false);
    } else {
      setFinished(true);
    }
  };

  const submitAttempt = async () => {
    setSubmitting(true);
    try {
      if (lessonId) {
        await api.post(`/lessons/${lessonId}/quiz-attempt`, {
          score,
          total: questions.length,
          student_id: defaultStudentId || chosenStudent || null,
        });
      }
      setSubmitted(true);
      const name = students.find((s) => s.id === chosenStudent)?.name;
      toast.success(name ? `Saved ${score}/${questions.length} for ${name}` : "Quiz score saved to dashboard");
      onQuizSubmitted && onQuizSubmitted();
    } catch (e) {
      toast.error("Could not save score");
    } finally {
      setSubmitting(false);
    }
  };

  if (!len) return null;
  const cur = scenes[Math.min(idx, len - 1)];
  const toggleBtn = "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors";

  return (
    <>
      <Canvas camera={{ position: [0, 1.2, 3.2], fov: 74 }} data-testid="vr-canvas-inner">
        <XR store={store}>
          <SceneContent
            sc={cur}
            sceneKey={idx}
            guided={guided}
            visibleHotspots={visibleHotspots}
            currentHotspot={currentHotspot}
            onHotspotOpen={narrateHotspot}
          />
          {guideData && (
            <GuideAvatar
              guide={guideData}
              speaking={speaking}
              label={guided && curBeat && curBeat.type !== "checkin" ? beatSpokenText(curBeat) : null}
            />
          )}
          <OrbitControls
            enablePan={false}
            minDistance={0.5}
            maxDistance={9}
            rotateSpeed={-0.4}
            autoRotate={autoRotate}
            autoRotateSpeed={0.3}
          />
        </XR>
      </Canvas>

      {/* Enter VR (WebXR) */}
      <button
        data-testid="vr-enter-headset-btn"
        onClick={enterVR}
        title={xrSupported ? "Open in your VR headset" : "No VR headset detected"}
        className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-slate-900 text-sm font-bold shadow-lg hover:bg-white transition-colors"
      >
        <Glasses className="w-4 h-4" /> Enter VR
      </button>

      {/* Scene navigation + narration overlay */}
      <div className="absolute inset-x-0 bottom-0 p-4 pointer-events-none">
        <div className="max-w-2xl mx-auto pointer-events-auto">
          <div className="glass rounded-2xl px-4 py-3 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <button
                data-testid="vr-prev-scene-btn"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className="w-9 h-9 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-opacity"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0 text-center">
                <div className="text-[11px] font-bold uppercase tracking-wider text-primary">Scene {idx + 1} / {len}</div>
                <div className="font-display font-semibold text-sm truncate">{cur.title}</div>
              </div>
              <button
                data-testid="vr-next-scene-btn"
                onClick={() => setIdx((i) => Math.min(len - 1, i + 1))}
                disabled={idx >= len - 1}
                className="w-9 h-9 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-opacity"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {!guided && cur.narration && <p className="text-xs text-muted-foreground mt-2 leading-snug line-clamp-2">{cur.narration}</p>}

            {guided ? (
              <div className="mt-3" data-testid="vr-timeline-panel">
                <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-primary">
                  <span className="flex items-center gap-1.5"><Route className="w-3.5 h-3.5" /> Step {clampedBeat + 1} / {beats.length}</span>
                  <button
                    data-testid="vr-timeline-exit-btn"
                    onClick={exitGuided}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground normal-case tracking-normal font-semibold"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Exit
                  </button>
                </div>
                <div className="h-1.5 bg-secondary rounded-full mt-1.5 overflow-hidden">
                  <div
                    data-testid="vr-timeline-progress"
                    className="h-full bg-primary rounded-full transition-[width] duration-500"
                    style={{ width: `${((clampedBeat + 1) / beats.length) * 100}%` }}
                  />
                </div>
                {curBeat?.type !== "checkin" && beatSpokenText(curBeat) && (
                  <p className="text-xs text-foreground/90 mt-2 leading-snug line-clamp-3" data-testid="vr-timeline-beat-text">
                    <span className="font-bold text-primary mr-1">{curBeat.type === "hotspot" ? "Discover:" : "Intro:"}</span>
                    {beatSpokenText(curBeat)}
                  </p>
                )}
                {curBeat?.type === "checkin" && (
                  <CheckinBeat key={`${idx}:${clampedBeat}`} beat={curBeat} onDone={respondCheckin} />
                )}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                  <button
                    data-testid="vr-timeline-back-btn"
                    onClick={() => setBeatIdx((b) => Math.max(0, Math.min(b, beats.length - 1) - 1))}
                    disabled={clampedBeat === 0}
                    className={`${toggleBtn} bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-40`}
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    data-testid="vr-timeline-replay-btn"
                    onClick={replayBeat}
                    className={`${toggleBtn} bg-secondary text-muted-foreground hover:text-foreground`}
                  >
                    <RotateCcw className={`w-4 h-4 ${speaking ? "animate-pulse" : ""}`} /> Replay
                  </button>
                  <button
                    data-testid="vr-timeline-mode-btn"
                    onClick={() => setCinematic((c) => !c)}
                    className={`${toggleBtn} ${cinematic ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}
                  >
                    {cinematic ? <Film className="w-4 h-4" /> : <Footprints className="w-4 h-4" />}
                    {cinematic ? "Cinematic" : "Self-paced"}
                  </button>
                  {curBeat?.type !== "checkin" && (
                    <button
                      data-testid="vr-timeline-next-btn"
                      onClick={advanceBeat}
                      disabled={clampedBeat >= beats.length - 1 && idx >= len - 1}
                      className={`${toggleBtn} bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40`}
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                  {questions.length > 0 && (
                    <button data-testid="vr-start-quiz-btn" onClick={startQuiz} className={`${toggleBtn} bg-emerald-500/90 text-white hover:bg-emerald-600`}>
                      <ClipboardList className="w-4 h-4" /> Quiz
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                  <button
                    data-testid="vr-guided-start-btn"
                    onClick={startGuided}
                    className={`${toggleBtn} bg-primary text-primary-foreground hover:opacity-90`}
                  >
                    <Route className="w-4 h-4" /> Guided lesson
                  </button>
                  <button
                    data-testid="vr-narrate-toggle-btn"
                    onClick={() => setNarrate((n) => !n)}
                    className={`${toggleBtn} ${narrate ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                  >
                    {narrate ? <Volume2 className={`w-4 h-4 ${speaking ? "animate-pulse" : ""}`} /> : <VolumeX className="w-4 h-4" />}
                    {narrate ? "Narrating" : "Narrate"}
                  </button>
                  <button
                    data-testid="vr-autoplay-tour-btn"
                    onClick={() => setTour((t) => !t)}
                    className={`${toggleBtn} ${tour ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                  >
                    {tour ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    {tour ? "Playing tour" : "Auto-play tour"}
                  </button>
                  {questions.length > 0 && (
                    <button data-testid="vr-start-quiz-btn" onClick={startQuiz} className={`${toggleBtn} bg-emerald-500 text-white hover:bg-emerald-600`}>
                      <ClipboardList className="w-4 h-4" /> Take the quiz
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* In-VR Quiz overlay */}
      {quizOpen && (
        <div data-testid="vr-quiz-overlay" className="absolute inset-0 z-30 bg-slate-950/85 backdrop-blur-md overflow-auto p-4 sm:p-6">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-white font-display font-bold">
                <ClipboardList className="w-5 h-5 text-emerald-400" /> Lesson Quiz
              </div>
              <button data-testid="vr-quiz-close-btn" onClick={() => setQuizOpen(false)} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!finished ? (
              <div className="rounded-2xl bg-white p-5 shadow-2xl animate-in-up">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Question {qIdx + 1} / {questions.length}</div>
                <p className="font-display font-semibold text-lg mt-1 text-slate-900">{questions[qIdx]?.question}</p>
                <div className="space-y-2 mt-4">
                  {(questions[qIdx]?.options || []).map((opt, i) => {
                    const isCorrect = optLetter(opt) === answerLetter(questions[qIdx]);
                    const isSel = selected === opt;
                    let cls = "border-slate-200 hover:border-emerald-400";
                    if (revealed && isCorrect) cls = "border-emerald-500 bg-emerald-50 text-emerald-800";
                    else if (revealed && isSel && !isCorrect) cls = "border-red-500 bg-red-50 text-red-800";
                    return (
                      <button
                        key={i}
                        data-testid={`vr-quiz-option-${i}`}
                        disabled={revealed}
                        onClick={() => pickOption(opt)}
                        className={`w-full text-left text-sm rounded-xl border-2 px-4 py-3 transition-colors flex items-center justify-between text-slate-800 ${cls}`}
                      >
                        <span>{opt}</span>
                        {revealed && isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                        {revealed && isSel && !isCorrect && <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {revealed && questions[qIdx]?.explanation && (
                  <p className="text-xs text-slate-600 mt-3 bg-slate-50 rounded-lg p-3">💡 {questions[qIdx].explanation}</p>
                )}
                {revealed && (
                  <button data-testid="vr-quiz-next-btn" onClick={nextQuestion} className="mt-4 w-full h-11 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors">
                    {qIdx < questions.length - 1 ? "Next question" : "See my score"}
                  </button>
                )}
              </div>
            ) : (
              <div className="rounded-2xl bg-white p-6 shadow-2xl text-center animate-in-up" data-testid="vr-quiz-result">
                <Award className="w-12 h-12 text-amber-500 mx-auto" />
                <div className="text-4xl font-display font-extrabold mt-3 text-slate-900">{score}/{questions.length}</div>
                <div className="text-emerald-600 font-bold text-lg">{Math.round((score / questions.length) * 100)}%</div>
                <p className="text-slate-500 text-sm mt-1">
                  {score === questions.length ? "Perfect score! 🎉" : score / questions.length >= 0.6 ? "Great work!" : "Keep exploring and try again!"}
                </p>

                {!submitted ? (
                  <div className="mt-5 text-left">
                    {students.length > 0 && !defaultStudentId && (
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-600">Record this score for</label>
                        <select
                          data-testid="vr-quiz-student-select"
                          value={chosenStudent}
                          onChange={(e) => setChosenStudent(e.target.value)}
                          className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm text-slate-800"
                        >
                          <option value="">Whole class (no individual)</option>
                          {students.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button data-testid="vr-quiz-retake-btn" onClick={startQuiz} className="flex-1 h-11 rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50">
                        Retake
                      </button>
                      <button data-testid="vr-quiz-submit-btn" disabled={submitting} onClick={submitAttempt} className="flex-1 h-11 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-600 disabled:opacity-60">
                        {submitting ? "Saving…" : "Save to dashboard"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="vr-quiz-done-btn" onClick={() => setQuizOpen(false)} className="mt-5 w-full h-11 rounded-full bg-slate-900 text-white font-bold">
                    Done
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
